import { Loader2, ShoppingCart, Plus, MessageCircle, Lock, Music2, Sparkles, Rocket } from 'lucide-react';
import { usePlatform } from '../../context/PlatformContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';

export function BookingDialog({ open, onOpenChange, artist, dubMessage, setDubMessage, songTitle, setSongTitle, onAddToCart, addingToCart, isInCart, effectivePrice, originalPrice, weeklyOverride }) {
  const price = effectivePrice != null ? effectivePrice : artist?.price;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-md" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Book {artist?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {weeklyOverride && (
            <div className="bg-lime/10 border border-lime/30 rounded-lg p-3 text-sm" data-testid="dialog-weekly-price">
              <div className="flex items-center gap-2 text-lime font-semibold mb-1">
                <Sparkles className="w-4 h-4" /> Weekly Specials price
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-mono font-bold text-lime">${Number(price).toFixed(2)}</span>
                {originalPrice != null && originalPrice !== price && (
                  <span className="text-xs text-[#A1A1AA] line-through">${Number(originalPrice).toFixed(2)} normally</span>
                )}
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="song-title" className="text-sm text-[#A1A1AA] mb-2 block">Song Title</Label>
            <Input
              id="song-title"
              value={songTitle}
              onChange={(e) => setSongTitle(e.target.value)}
              placeholder="Enter the song title here"
              className="input-dark"
              maxLength={120}
              data-testid="song-title-input"
            />
          </div>
          <div>
            <Label htmlFor="dub-message" className="text-sm text-[#A1A1AA] mb-2 block">Custom Dubplate Message</Label>
            <p className="text-xs text-[#666] mb-2">Tell the artist what you want them to say:</p>
            <Textarea
              id="dub-message"
              value={dubMessage}
              onChange={(e) => setDubMessage(e.target.value)}
              placeholder="e.g., Big up to DJ Flame, running things in Miami! Tune4Tune exclusive!"
              className="input-dark min-h-[120px]"
              maxLength={500}
              data-testid="dub-message-input"
            />
            <p className="text-xs text-[#666] mt-1">{dubMessage.length}/500 characters</p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#333]">Cancel</Button>
          <Button onClick={onAddToCart} disabled={addingToCart || !dubMessage.trim() || !songTitle.trim()} className="btn-primary" data-testid="confirm-add-cart-btn">
            {addingToCart ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShoppingCart className="w-4 h-4 mr-2" />}
            Add to Cart
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BookingSidebar({ artist, canBook, canSeePrices, canMessage, isArtist, isInCart, onBookClick, startingConversation, onMessageClick, effectivePrice, originalPrice, weeklyOverride }) {
  const { ordersEnabled } = usePlatform();
  const price = effectivePrice != null ? effectivePrice : artist.price;
  return (
    <div className="space-y-6">
      {/* Price Card */}
      <div className="bg-surface border border-[#262626] rounded-xl p-6 sticky top-28" data-testid="booking-sidebar">
        <div className="text-center mb-6">
          {canSeePrices ? (
            <>
              {weeklyOverride && (
                <div className="inline-flex items-center gap-1 text-xs text-lime font-semibold mb-2" data-testid="sidebar-weekly-tag">
                  <Sparkles className="w-3.5 h-3.5" /> WEEKLY SPECIALS
                </div>
              )}
              <p className="text-4xl font-mono font-bold text-lime">${Number(price).toFixed(2)}</p>
              {weeklyOverride && originalPrice != null && originalPrice !== price && (
                <p className="text-xs text-[#A1A1AA] line-through mt-1" data-testid="sidebar-original-price">
                  Regular price ${Number(originalPrice).toFixed(2)}
                </p>
              )}
              <p className="text-sm text-[#A1A1AA] mt-1">per dubplate</p>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Lock className="w-8 h-8 text-[#555]" />
              <p className="text-sm text-[#A1A1AA]">{isArtist ? 'Artist accounts cannot see prices' : 'Login to see pricing'}</p>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="flex justify-between text-sm"><span className="text-[#A1A1AA]">Delivery Time</span><span>{artist.delivery_days} days</span></div>
          <div className="flex justify-between text-sm"><span className="text-[#A1A1AA]">Format</span><span>High-Quality Audio</span></div>
          <div className="flex justify-between text-sm"><span className="text-[#A1A1AA]">Custom Message</span><span className="text-lime">Included</span></div>
        </div>

        <div className="mt-6 space-y-3">
          {!ordersEnabled ? (
            <div className="text-center space-y-2" data-testid="prelaunch-cta">
              <Button disabled className="w-full bg-[#262626] text-[#A1A1AA] cursor-not-allowed">
                <Rocket className="w-4 h-4 mr-2" />Launching Soon
              </Button>
              <p className="text-xs text-[#666]">You will be notified when Tune4Tune launches</p>
            </div>
          ) : (
            <>
              {canBook && (
                <Button onClick={onBookClick} className="w-full btn-primary" data-testid="book-artist-btn">
                  {isInCart ? <><Plus className="w-4 h-4 mr-2" />Add Another</> : <><Plus className="w-4 h-4 mr-2" />Add to Cart</>}
                </Button>
              )}
              {canMessage && (
                <Button onClick={onMessageClick} disabled={startingConversation} variant="outline" className="w-full border-[#333] hover:border-lime" data-testid="message-artist-btn">
                  {startingConversation ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageCircle className="w-4 h-4 mr-2" />}
                  Message Artist
                </Button>
              )}
              {!canBook && !canMessage && (
                <p className="text-center text-sm text-[#A1A1AA]">
                  {isArtist ? 'Artist accounts cannot book other artists' : 'Login to book this artist'}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Genres */}
      {artist.genres?.length > 0 && (
        <div className="bg-surface border border-[#262626] rounded-xl p-6">
          <h3 className="font-heading font-bold mb-3 flex items-center gap-2"><Music2 className="w-4 h-4 text-lime" />Genres</h3>
          <div className="flex flex-wrap gap-2">
            {artist.genres.map(genre => (
              <span key={genre} className="px-3 py-1 bg-lime/10 text-lime text-sm rounded-full">{genre}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
